<template>
    <div>
        <div class="read-only-editor" style="background: white;">
            <div>
                <p :class="{'hidden': this.loading === false}" class="text-center"
                   style="position:absolute; top: 45%; width: 100%;">
                <span class="loading-spinner loading-spinner-big loading-spinner-green"
                      style="color: #41a499; font-family: FontAwesome !important;"></span>
                </p>
            </div>
            <div ref="editor" class="editor_inner" :style="{'height': height}"
                 id="read_only_editor"></div>
            <div ref="hiddenEditor" style="display: none"></div>
        </div>
    </div>
</template>

<script>
    import * as monaco from 'monaco-editor-core'
    import {isMobileDevice} from "../../services/Utilities";
    import {SIGNS} from "../../const";

    // supported programming-languages
    import 'monaco-languages/release/esm/javascript/javascript.contribution'
    import 'monaco-languages/release/esm/java/java.contribution'
    import 'monaco-languages/release/esm/python/python.contribution'
    import 'monaco-languages/release/esm/php/php.contribution'
    import 'monaco-languages/release/esm/csharp/csharp.contribution'
    import 'monaco-languages/release/esm/cpp/cpp.contribution'
    import 'monaco-languages/release/esm/dockerfile/dockerfile.contribution'

    self.MonacoEnvironment = {
        getWorkerUrl: function (moduleId, label) {
            if (label === "json") {
                return '/json.worker.bundle.' + process.env.VERSION + '.js';
            }
            return '/editor.worker.bundle.' + process.env.VERSION + '.js';
        }
    };

    export default {
        name: "quality-editor",
        mounted() {
            this.createMonaco();
            this.editor.onDidFocusEditorText(function () {
                this.$emit('focused')
            }.bind(this));

        },
        destroyed() {
            this.codeModel.dispose();
            this.editor.dispose();
        },
        props: {
            language: {type: String, default: ''},
            code: {type: String},
            previousCode: {type: String},
            height: {type: String, default: '45vh'},
            minimap: {type: Boolean, default: true},
            filePath: {type: String, default: null},
            folding: {type: Boolean, default: true},
        },
        data() {
            return {
                loading: true,
                current_style: null,
                editorWorkerService: null,
                lastRangeChanged: new monaco.Range(0, 0, 0, 0),
                decorations: [],
                editor: null,
                codeModel: null,
            }
        },
        watch: {
            code: function (code) {
                let protectedCode = this.getProtectedCode(this.code);

                if (!this.editor) {
                    return;
                }

                let modified = monaco.editor.createModel(protectedCode, this.language);
                let original = monaco.editor.createModel(this.previousCode);

                let oldEndLine = this.editor.getModel().getLineCount();
                let oldMaxColumn = this.editor.getModel().getLineMaxColumn(oldEndLine);

                // We need this, to avoid flickering and deletion of decorations
                // see https://github.com/Microsoft/monaco-editor/issues/803
                this.editor.getModel().applyEdits([
                    {
                        forceMoveMarkers: false,
                        range: new monaco.Range(
                            0, 0,
                            oldEndLine, oldMaxColumn
                        ),
                        text: protectedCode
                    }
                ]);
                this.editor.getModel().forceTokenization(modified.getLineCount());

                this.editorWorkerService.computeDiff(original.uri, modified.uri, true).then((result) => {
                    if (result === null|| !result.changes || result.changes.length < 1) {
                        return;
                    }
                    let startLineNumber = result.changes[0].modifiedStartLineNumber;
                    let endLineNumber = result.changes[0].modifiedEndLineNumber;
                    if (typeof startLineNumber === "undefined" || typeof endLineNumber === "undefined") {
                        return;
                    }

                    this.editor.revealLinesInCenterIfOutsideViewport(startLineNumber, endLineNumber, 0);

                    this.decorations = this.editor.deltaDecorations(this.decorations, [
                        {
                            range: new monaco.Range(startLineNumber, 0, endLineNumber, modified.getLineMaxColumn(endLineNumber)),
                            options: {marginClassName: 'changedLine', isWholeLine: true}
                        },
                    ]);
                }, (error) => {
                    console.error(error);
                });
            }
        },
        methods: {
            getProtectedCode(code) {
                let protectedCode = code;
                for (const sign of SIGNS) {
                    protectedCode = protectedCode.replace(sign.replacement, sign.sign);
                }
                return protectedCode;
            },
            renderComments(comments) {
                const commentDecorationList = [];
                for (let comment of comments) {
                    let range = new monaco.Range(comment.range.startLineNumber, comment.range.startColumn, comment.range.endLineNumber, comment.range.endColumn);
                    if (range.startLineNumber === range.endLineNumber && range.startColumn === range.endColumn) {
                        range = new monaco.Range(range.startLineNumber, 0, range.endLineNumber + 1, 0);
                    }
                    commentDecorationList.push(
                        {
                            range: range,
                            options: {
                                inlineClassName: 'codeCommentInline',
                                glyphMarginClassName: 'commentGlyphMarginClass',
                                glyphMarginHoverMessage: {
                                    value: `![](${comment.user.userImage})    __${comment.user.userName}:__ ${comment.message}'`
                                },
                                hoverMessage: {
                                    value: `![](${comment.user.userImage})    __${comment.user.userName}:__ ${comment.message}'`
                                }
                            }
                        },
                    )
                }
                const addButtons = this.getCodeCommentAddButtonsForRendering();
                const finalDecorationList = addButtons.concat(commentDecorationList);
                if (finalDecorationList.length > 0) {
                    this.decorations = this.editor.deltaDecorations(this.decorations, finalDecorationList);
                }
            },
            removeComments(){
              this.decorations = this.editor.deltaDecorations(this.decorations, []);
            },
            highlightComment(range) {
                const commentDecorationList = [];
                if (range.startLineNumber === range.endLineNumber && range.startColumn === range.endColumn) {
                    range = new monaco.Range(range.startLineNumber, 0, range.endLineNumber + 1, 1);
                }
                commentDecorationList.push(
                    {
                        range: new monaco.Range(range.startLineNumber, range.startColumn, range.endLineNumber, range.endColumn),
                        options: {
                            inlineClassName: 'codeCommentHighlightInline',
                            glyphMarginClassName: 'commentHighlightGlyphMarginClass',
                        }
                    },
                );
                this.decorations = this.editor.deltaDecorations(this.decorations, commentDecorationList);
            },
            editorHasLoaded(editor, monaco) {
                this.editor = editor;
                this.monaco = monaco;
                this.setEditorTheme(monaco);
                this.loading = false;
                // manual trigger window resize event otherwise the editor not fits the content (rank the code details page)
                setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                }, 1000)

                this.$emit('loaded', true);
            },
            createMonaco() {
                let diffEditor = monaco.editor.createDiffEditor(this.$refs.hiddenEditor);
                this.editorWorkerService = diffEditor._editorWorkerService;

                const scrollbar = {
                    useShadows: false,
                    verticalHasArrows: true,
                    horizontalHasArrows: true,
                    vertical: 'auto',
                    horizontal: 'auto',
                    verticalScrollbarSize: 17,
                    horizontalScrollbarSize: 17,
                    arrowSize: 30
                };
                this.editor = monaco.editor.create(this.$refs.editor, {
                    theme: 'vs-dark',
                    automaticLayout: true,
                    readOnly: true,
                    enableSplitViewResizing: false,
                    glyphMargin: true,
                    minimap: {
                        enabled: this.minimap,
                        renderCharacters: false
                    },
                    scrollbar: scrollbar,
                    lineNumbersMinChars: 1,
                    folding: this.folding,
                    selectOnLineNumbers: false,
                    fontFamily: "Source Code Pro, Courier New, Courier, monospace",
                    fontSize: isMobileDevice() ? 10 : 12,
                });

                let protectedCode = this.getProtectedCode(this.code);
                if (this.language) {
                    this.codeModel = monaco.editor.createModel(protectedCode, this.language);
                } else if (this.filePath) {
                    this.codeModel = monaco.editor.createModel(protectedCode, '', monaco.Uri.file(this.filePath));
                } else {
                    throw "One of the following is a required prop [language, filePath]"
                }

                this.editor.setModel(this.codeModel);
                this.editorHasLoaded(this.editor, monaco);
            },
            initializeCodeCommentAddButtons: function () {
                this.editor.onMouseDown(function (event) {
                    const checkIfIsAddCommentButton = event.target.element.classList.contains("addCommentButton");
                    if (!checkIfIsAddCommentButton) {
                        return;
                    }
                    // check if the editor line is outside the selection range
                    if (event.target.range.startLineNumber < this.editor.getSelection().startLineNumber || event.target.range.startLineNumber > this.editor.getSelection().endLineNumber) {
                        this.editor.setSelection(event.target.range);
                    }
                    this.$emit("addCommentButtonClicked");
                }.bind(this));
            },
            getCodeCommentAddButtonsForRendering: function () {
                let addCommentButtonDecorations = [];
                for (let lineIndex = 0; lineIndex <= this.editor.getModel().getLineCount(); lineIndex++) {
                    addCommentButtonDecorations.push(
                        {
                            range: new monaco.Range(lineIndex, 0, lineIndex, 1),
                            options: {
                                beforeContentClassName: 'addCommentButton',
                                isWholeLine: true,
                            }
                        },
                    );
                }
                return addCommentButtonDecorations;
            },
            setEditorTheme: function (monaco) {
                //TODO We need to store this externally
                monaco.editor.defineTheme('polylith', {
                    base: 'vs',
                    inherit: true,
                    rules: [
                        {
                            background: 'e2e9ff',
                            token: 'text.html source.active4d'
                        },
                        {
                            foreground: '000000',
                            token: 'text.xml'
                        },
                        {
                            foreground: 'af82d4',
                            token: 'comment.line'
                        },
                        {
                            foreground: 'af82d4',
                            token: 'comment.block'
                        },
                        {
                            foreground: '329998',
                            token: 'string'
                        },
                        {
                            foreground: '66ccff',
                            fontStyle: 'bold',
                            token: 'string.interpolated variable'
                        },
                        {
                            foreground: 'a8017e',
                            token: 'constant.numeric'
                        },
                        {
                            foreground: '66ccff',
                            fontStyle: 'bold',
                            token: 'constant.other.date'
                        },
                        {
                            foreground: '66ccff',
                            fontStyle: 'bold',
                            token: 'constant.other.time'
                        },
                        {
                            foreground: 'a535ae',
                            token: 'constant.language'
                        },
                        {
                            foreground: '6392ff',
                            fontStyle: 'bold',
                            token: 'variable.other.local'
                        },
                        {
                            foreground: '0053ff',
                            fontStyle: 'bold',
                            token: 'variable'
                        },
                        {
                            foreground: '6988ae',
                            token: 'variable.other.table-field'
                        },
                        {
                            foreground: '5463e5',
                            fontStyle: 'bold',
                            token: 'keyword'
                        },
                        {
                            foreground: 'ff5600',
                            token: 'storage'
                        },
                        {
                            foreground: '21439c',
                            token: 'entity.name.type'
                        },
                        {
                            foreground: '21439c',
                            token: 'entity.name.function'
                        },
                        {
                            foreground: '7a7a7a',
                            token: 'meta.tag'
                        },
                        {
                            foreground: '016cff',
                            token: 'entity.name.tag'
                        },
                        {
                            foreground: '963dff',
                            token: 'entity.other.attribute-name'
                        },
                        {
                            foreground: '45ae34',
                            fontStyle: 'bold',
                            token: 'support.function'
                        },
                        {
                            foreground: 'b7734c',
                            token: 'support.constant'
                        },
                        {
                            foreground: 'a535ae',
                            token: 'support.type'
                        },
                        {
                            foreground: 'a535ae',
                            token: 'support.class'
                        },
                        {
                            foreground: 'a535ae',
                            token: 'support.variable'
                        },
                        {
                            foreground: 'ffffff',
                            background: '990000',
                            token: 'invalid'
                        },
                        {
                            foreground: 'ffffff',
                            background: '656565',
                            token: 'meta.diff'
                        },
                        {
                            foreground: 'ffffff',
                            background: '1b63ff',
                            token: 'meta.diff.range'
                        },
                        {
                            foreground: '000000',
                            background: 'ff7880',
                            token: 'markup.deleted.diff'
                        },
                        {
                            foreground: '000000',
                            background: '98ff9a',
                            token: 'markup.inserted.diff'
                        },
                        {
                            foreground: '5e5e5e',
                            token: 'source.diff'
                        }
                    ],
                });
                monaco.editor.setTheme('polylith');
            }
        }

    }
</script>

<style>
    .main-content {
        padding-bottom: 0;
    }

    .changedLine {
        background: #08AB99;
    }

    .codeCommentInline {
        cursor: pointer;
        background-color: rgba(241, 196, 15, 0.4);
    }

    .codeCommentMargin:after {
        font-family: "FontAwesome";
        content: "\f0e0";
        color: #08AB99;
        cursor: pointer !important;
    }

    .hover-row img {
        width: 25px;
    }

    .hover-row * {
        font-family: "Open Sans", sans-serif !important;
    }

    .changedLine + .line-numbers {
        color: white !important;
    }

    .myLineDecoration {
        background: lightblue;
        width: 50px !important;
        margin-left: 3px;
    }

    .codeCommentHighlightInline {
        cursor: pointer;
        background-color: rgba(241, 196, 15, 0.7);
    }

    .monaco-editor-background {
        overflow: visible !important;
        contain: none !important;
    }

    .monaco-scrollable-element {
        overflow: visible !important;
    }

    .view-line .addCommentButton:before {
        opacity: 1;
        font-family: FontAwesome !important;
        content: '\f0fe';
        cursor: pointer;
        position: absolute;
        font-size: 1.5rem;
    }

    .addCommentButton {
        position: absolute;
        z-index: 1000000000000000000000000000;
        opacity: 0;
        left: -19px;
        width: 18px;
        display: block;
        height: 18px;
    }

    .addCommentButton:before {
        position: relative;
    }

    .view-line:hover .addCommentButton {
        opacity: 1;
        position: absolute;
    }

    .view-line:hover .addCommentButton:before {
        opacity: 1;
        font-family: FontAwesome !important;
        content: '\f067';
        color: white;
        width: 1.5rem;
        line-height: 1.5rem;
        text-align: center;
        background-color: rgb(84, 99, 229);
        border-radius: 3px;
        cursor: pointer;
        position: absolute;
        font-size: 1.0rem;
        transition: transform 0.1s ease;
    }

    .addCommentButton:hover:before {
        transform: scale(1.5);
    }


    .monaco-editor .view-overlays .current-line {
        border: none !important;
    }

    .line-numbers {
        pointer-events: none;
    }

    @media (max-width: 400px) {
        #read_only_editor textarea {
            display: none !important;
        }
    }
</style>
